import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { useSession } from "../auth/session";
import { redeemEmailLink } from "../auth/emailLink";
import { setTokens } from "../auth/tokenStore";
import { Role } from "../auth/capabilities";
import { resolveTenantSlug, tenantPath } from "../config";
import { parseRespondPath, withReturnTo } from "../auth/questionnaireReturnTo";

// Redeem target of the emailed one-time link (E1 US-1.3). On mount it exchanges
// the token for a learner session and lands on the home view ("etusivu"). The
// interrupting/unhappy states — expired or already-used link, unknown tenant —
// render a clear message and establish NO session. The language chosen earlier
// persists here via localStorage, so an error page reads in the learner's
// language across the redirect.
//
// `rate-limited` (task da77004b) is the per-address limiter refusing this redeem
// (RateLimitPolicies.PublicEmailLink). It is deliberately NOT `expired`: the
// limiter short-circuits before the handler, so the one-time token was never
// looked at and is still unspent — this link works again once the window turns,
// which is why this state offers a plain retry instead of "request a new link".
// Both halves of that are measured against the real stack in
// e2e/realstack/public-email-link-budget.realstack.spec.ts — this banner and its
// retry under a real 429, and the same token redeeming into a session afterwards.
type Status = "redeeming" | "expired" | "unknown-tenant" | "rate-limited" | "error";

export function EmailLinkRedeemPage() {
  const { t } = useTranslation();
  const { setSession } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tenant = resolveTenantSlug();
  const token = params.get("token") ?? "";
  const [status, setStatus] = useState<Status>("redeeming");
  // A one-time token must be redeemed exactly once — guard against React's
  // double effect invocation (StrictMode) firing a second, now-used redeem.
  const redeemed = useRef(false);
  // WHERE THIS LINK WAS GOING (task ec54522c). A questionnaire invitation to an external learner carries
  // this redeem route with a `returnTo` of `/questionnaires/<id>/respond` beside the one-time token, so the
  // redeem opens the form rather than the home view — the whole point of a personal sign-in link. Validated to
  // exactly that one shape by `parseRespondPath`, because this value came out of a URL; anything else is
  // dropped and the landing is "/", as it has always been for a self-service link.
  const returnTo = parseRespondPath(params.get("returnTo"));
  // Every way OUT of this page keeps the tenant the URL named (task f390743e). The mailed link is
  // `/t/<slug>/login/email/redeem`, and on the apex host — how production addresses every tenant — a bare
  // `/login/email` here is a request page that can resolve no organisation and says "Organisaatiota ei
  // löytynyt": the dead end this page's own refusals used to offer as the way back.
  //
  // …and it keeps the DESTINATION too. A learner whose questionnaire link has stopped working is still on
  // their way to that questionnaire, so the fresh link they ask for carries it (`withReturnTo` → the
  // request page → the mailed URL). Note that the refusals below offer only this door and never
  // `/login`: an email-link learner has no SSO account, and an SSO button is the dead end they met
  // before this capability existed. The one exception is `unknown-tenant`, where the slug in the URL
  // names no organisation — there is no email-link request to offer for an organisation that does not
  // exist, and that state's own message says so.
  const requestLink = withReturnTo(tenantPath("/login/email"), returnTo);
  const loginLink = tenantPath("/login");

  // One redeem attempt. Extracted so the rate-limited state can offer a retry:
  // a 429 never reached the handler, so the token is unspent and trying the same
  // one again is correct — unlike every other failure here, which is terminal.
  async function attempt() {
    if (!token) {
      setStatus("expired");
      return;
    }
    setStatus("redeeming");
    const outcome = await redeemEmailLink(token);
    if (outcome.kind === "ok") {
      // Persist the opaque `clsk_` session as the bearer so every subsequent API call carries
      // `Authorization: Bearer clsk_…` (lib/api attaches it). Without this the learner would "reach
      // home" only to 401 on the first data fetch. A real backend 401 (revoked/expired session) still
      // drives the session-expired redirect, so we hand the token store the server's own expiry.
      const expiresAt = outcome.expiresAt ? Date.parse(outcome.expiresAt) : NaN;
      setTokens({
        accessToken: outcome.sessionToken,
        expiresAt: Number.isNaN(expiresAt) ? Date.now() + 60 * 60 * 1000 : expiresAt,
      });
      setSession({
        userId: "external-learner",
        displayName: t("emailLink.learnerName"),
        tenantSlug: tenant,
        // External learners are learners only — they never see admin surfaces.
        roles: [Role.Learner],
      });
      // The destination the mailed link named, or the home view. `replace`, so Back leaves the app
      // rather than re-entering a redeem whose token this attempt has already used.
      void navigate(returnTo ?? "/", { replace: true });
      return;
    }
    setStatus(outcome.kind);
  }

  useEffect(() => {
    // The ref dedupes the redeem so a one-time token is spent exactly once,
    // even under React's double effect invocation (StrictMode). No abort-on-
    // cleanup: cancelling would risk a half-consumed token and, under StrictMode,
    // strand the view in the loading state. The retry button below goes straight
    // to `attempt()`: it is a deliberate second try of a token the limiter never
    // let through, which is the one case a second redeem is right.
    if (redeemed.current) return;
    redeemed.current = true;

    void attempt();
    // Runs once on mount; token/tenant are captured from the entry URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout>
      {status === "redeeming" && (
        <p className="auth__status" role="status">
          <span className="auth__spinner" aria-hidden="true" />
          {t("emailLink.redeeming")}
        </p>
      )}

      {status === "expired" && (
        <>
          <p role="alert" className="banner banner--error">
            {t("emailLink.expiredBody")}
          </p>
          <div className="auth__actions">
            <Link className="btn btn--primary btn--block" to={requestLink}>
              {t("emailLink.requestNewLink")}
            </Link>
          </div>
        </>
      )}

      {status === "unknown-tenant" && (
        <>
          <p role="alert" className="banner banner--error">
            {t("states.unknownTenant")}
          </p>
          <div className="auth__actions">
            <Link className="btn btn--block" to={loginLink}>
              {t("emailLink.backToLogin")}
            </Link>
          </div>
        </>
      )}

      {status === "rate-limited" && (
        <>
          <p role="alert" className="banner banner--warn" data-testid="email-link-rate-limited">
            {t("states.tooManyRequests")}
          </p>
          <div className="auth__actions">
            <button
              type="button"
              className="btn btn--primary btn--block"
              onClick={() => void attempt()}
            >
              {t("emailLink.retry")}
            </button>
          </div>
        </>
      )}

      {status === "error" && (
        <>
          <p role="alert" className="banner banner--error">
            {t("states.genericError")}
          </p>
          <div className="auth__actions">
            <Link className="btn btn--primary btn--block" to={requestLink}>
              {t("emailLink.requestNewLink")}
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
